import { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, MapPin, TrendingUp, Building2, CheckCircle2, Sparkles, Calculator, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const CITIES = {
  paris: {
    name: 'Paris',
    region: 'Île-de-France',
    cp: '75000',
    tagline: "Marchand de biens à Paris & petite couronne — division, rénovation, revente",
    hero_image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600",
    volumes: { biens: '450k', avg_price_sqm: '10 500', avg_rent_sqm: '32' },
    neighborhoods: ['11e', '18e', '19e', '20e', 'Montreuil', 'Pantin', 'Saint-Ouen'],
    seo_desc: "Vous détenez un bien à Paris ou en petite couronne et souhaitez le valoriser ? G Division rachète directement les biens à fort potentiel de division ou rénovation. Prix juste, sans négociation interminable.",
    local_angle: "Paris reste le marché le plus liquide d'Europe pour la division immobilière. Un pavillon de 150m² à Paris 19e peut dégager jusqu'à 40% de marge en division en 3 appartements."
  },
  lyon: {
    name: 'Lyon',
    region: 'Auvergne-Rhône-Alpes',
    cp: '69000',
    tagline: "Marchand de biens à Lyon et agglomération — opérations rapides, offre ferme sous 7 jours",
    hero_image: "https://images.unsplash.com/photo-1545158539-6bf34152b020?w=1600",
    volumes: { biens: '280k', avg_price_sqm: '5 200', avg_rent_sqm: '15' },
    neighborhoods: ['3e', '7e', '8e', 'Villeurbanne', 'Vaise', 'Croix-Rousse'],
    seo_desc: "G Division opère sur Lyon et la métropole. Nous rachetons votre bien directement — maison, immeuble, terrain — pour des opérations de division et rénovation. Transaction rapide, sans aller-retour.",
    local_angle: "Lyon offre un ratio prix/potentiel exceptionnel. Les immeubles pré-haussmanniens des 3e et 7e arrondissements sont particulièrement propices aux divisions en 2-4 lots."
  },
  chambery: {
    name: 'Chambéry',
    region: 'Savoie',
    cp: '73000',
    tagline: "Marchand de biens à Chambéry — opérations de division dans les Alpes",
    hero_image: "https://images.unsplash.com/photo-1569241751658-ab12f8ae9a85?w=1600",
    volumes: { biens: '45k', avg_price_sqm: '3 400', avg_rent_sqm: '12' },
    neighborhoods: ['Centre', 'Chambéry-le-Haut', 'Mérande', 'La Motte-Servolex', 'Cognin'],
    seo_desc: "G Division est présent à Chambéry et dans les communes limitrophes. Nous rachetons les biens à potentiel pour mener des opérations de valorisation : division parcellaire, création de logements, rénovation lourde.",
    local_angle: "Chambéry bénéficie d'une demande locative tendue (étudiants, actifs du bassin savoyard). Un immeuble de 300m² en centre-ville peut être redistribué en 5-6 logements avec un cashflow mensuel immédiat."
  },
  annecy: {
    name: 'Annecy',
    region: 'Haute-Savoie',
    cp: '74000',
    tagline: "Marchand de biens à Annecy — valorisation en zone tendue",
    hero_image: "https://images.unsplash.com/photo-1577086664693-894d8405334a?w=1600",
    volumes: { biens: '60k', avg_price_sqm: '6 800', avg_rent_sqm: '19' },
    neighborhoods: ['Annecy-le-Vieux', 'Cran-Gevrier', 'Seynod', 'Pringy', 'Vieille Ville', 'Albigny'],
    seo_desc: "Annecy est l'une des villes les plus demandées de France. G Division y rachète les biens à transformer : maisons familiales, immeubles anciens, terrains constructibles. Prix du marché, offre ferme sous 7 jours.",
    local_angle: "La rareté foncière à Annecy fait de chaque division une opération ultra-rentable. Les biens avec terrain >400m² en zone UB ou UC sont systématiquement intéressants."
  },
};

const CityLanding = ({ city }) => {
  const data = CITIES[city?.toLowerCase()];
  const canonical = `https://divisionimmobiliere.com/marchand-de-biens-${city?.toLowerCase() || ''}`;
  const pageTitle = data ? `Marchand de biens ${data.name} — Rachat direct, division, rénovation | G Division` : '';
  const pageDesc = data ? `G Division, marchand de biens à ${data.name} (${data.region}). Rachat direct de maisons, immeubles et terrains à potentiel de division. Offre ferme sous 7 jours.` : '';
  const jsonLd = data ? JSON.stringify({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `G Division — Marchand de biens ${data.name}`,
    "description": data.seo_desc,
    "areaServed": { "@type": "City", "name": data.name },
    "url": canonical,
    "telephone": "+33645040975"
  }) : '';

  useEffect(() => {
    if (!data) return;
    document.title = pageTitle;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', pageDesc);
    setMeta('keywords', `marchand de biens ${data.name}, achat bien ${data.name}, vendre maison ${data.name}, division immobilière ${data.name}, racheter immeuble ${data.name}, marchand biens ${data.region}`);
    setMeta('og:title', `Marchand de biens ${data.name} | G Division`, 'property');
    setMeta('og:description', data.seo_desc, 'property');
    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);
    // JSON-LD
    const existingLd = document.getElementById('city-ld');
    if (existingLd) existingLd.remove();
    const script = document.createElement('script');
    script.id = 'city-ld';
    script.type = 'application/ld+json';
    script.textContent = jsonLd;
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById('city-ld');
      if (s) s.remove();
    };
  }, [pageTitle, pageDesc, data, canonical, jsonLd]);

  if (!data) return <Navigate to="/nos-biens" replace />;

  return (
    <>
      <div className="pt-20 bg-white">
        {/* Hero */}
        <section className="relative h-[55vh] md:h-[65vh] overflow-hidden">
          <img src={data.hero_image} alt={data.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1f33]/60 via-[#0f1f33]/70 to-[#0f1f33]" />
          <div className="relative max-w-6xl mx-auto px-4 md:px-8 h-full flex flex-col justify-end pb-12">
            <div className="inline-flex items-center gap-2 bg-[#c9a227]/20 border border-[#c9a227]/40 text-[#c9a227] px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 w-fit">
              <MapPin size={12} /> {data.name} · {data.region}
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">
              Marchand de biens<br/>
              <span className="bg-gradient-to-r from-[#e0c062] to-[#c9a227] bg-clip-text text-transparent">à {data.name}</span>
            </h1>
            <p className="text-gray-200 text-base md:text-lg max-w-2xl">{data.tagline}</p>
          </div>
        </section>

        {/* Market data */}
        <section className="py-10 md:py-14 bg-gray-50 border-y border-gray-200">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-6">Marché {data.name} en bref</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">Logements</div>
                <div className="text-3xl font-bold text-[#1e3a5f] font-mono">{data.volumes.biens}</div>
                <div className="text-xs text-gray-500">Parc total ville + agglo</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">Prix moyen m²</div>
                <div className="text-3xl font-bold text-[#c9a227] font-mono">{data.volumes.avg_price_sqm} €</div>
                <div className="text-xs text-gray-500">Ancien, moyenne ville</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">Loyer m²/mois</div>
                <div className="text-3xl font-bold text-emerald-700 font-mono">{data.volumes.avg_rent_sqm} €</div>
                <div className="text-xs text-gray-500">Longue durée, non meublé</div>
              </div>
            </div>
            <p className="mt-6 text-gray-700 leading-relaxed max-w-3xl">{data.local_angle}</p>
          </div>
        </section>

        {/* Zones */}
        <section className="py-10 md:py-14">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-4">Nos zones d'intervention à {data.name}</h2>
            <p className="text-gray-600 mb-5">Nous rachetons principalement dans ces secteurs — mais nous étudions toute proposition sur {data.region}.</p>
            <div className="flex flex-wrap gap-2">
              {data.neighborhoods.map(n => (
                <span key={n} className="px-4 py-2 bg-gray-100 text-[#1e3a5f] rounded-full text-sm font-medium">{n}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-10 md:py-14 bg-gray-50 border-y border-gray-200">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-8">Ce que nous rachetons à {data.name}</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { icon: Building2, title: "Immeubles entiers", desc: "Pré-haussmanniens, années 60-70, à diviser en 2-6 lots" },
                { icon: TrendingUp, title: "Maisons à diviser", desc: "Pavillons avec surface et terrain pour horizontale ou verticale" },
                { icon: Sparkles, title: "Terrains à potentiel", desc: "Constructibles, divisibles, à viabiliser" },
              ].map(s => (
                <div key={s.title} className="bg-white rounded-xl p-6 border border-gray-200">
                  <s.icon size={28} className="text-[#c9a227] mb-3" />
                  <h3 className="font-bold text-[#1e3a5f] mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tool CTA */}
        <section className="py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#0f1f33] rounded-2xl p-6 md:p-8 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl font-bold mb-3">Testez votre bien à {data.name} en 3 minutes</h2>
              <p className="text-gray-300 mb-5">Diagnostic gratuit : score de faisabilité 0-100, verdict argumenté, estimation du gain potentiel.</p>
              <Link to="/nos-outils/diagnostic-faisabilite-division">
                <Button className="bg-[#c9a227] hover:bg-[#b08d22] text-white rounded-full px-8 py-6 text-sm font-bold uppercase tracking-wider">
                  <Calculator size={16} className="mr-2" />
                  Lancer le diagnostic
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f] mb-4">Vous avez un bien à vendre à {data.name} ?</h2>
            <p className="text-gray-600 mb-6">
              Décrivez-nous votre bien en quelques lignes. Nous revenons vers vous sous 48h avec une proposition claire.
              Sans visite imposée, sans négociation interminable.
            </p>
            <Link to="/contact">
              <Button className="bg-[#1e3a5f] hover:bg-[#0f1f33] text-white rounded-full px-10 py-6 text-sm font-bold uppercase tracking-wider">
                Nous contacter
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default CityLanding;
